import React, { useEffect, useState, useRef } from "react";
import { CommentWrapper, CommentHeader, CommentActions, CommentInfo, CommentContent, CommentReplies, CollapsedMessage } from "./styles.js";
import ReplyBox from "./ReplyBox";
import classNames from "classnames";
import DotsActionMenu from "../DotsActionMenu/index.js";
import InlineSVG from "react-inlinesvg";
import IconButton from "../IconButton/index.jsx";
import Reply from "./Reply.js";
import { useCommentStore } from "../../stores/commentStore";
import { EditableDisplayName } from "../ShareLinkPage/EditableDisplayName";
import CommentContentEditor from "./CommentContentEditor";
import InlineDiffViewer from "./InlineDiffViewer";
import * as Sentry from "@sentry/react";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip/index.tsx";
import { SudoButton } from "../../sudokit/components/SudoButton";
import { Textarea } from "../../sudokit/components/Textarea/index.jsx";
import Auth from "src/Auth.js";
import { useToast } from "../ToastWrapper/index.js";
import { resolveFeedbackHeader } from "src/shared/feedbackCategories";
import { applySuggestionToEditor } from "src/utils/tiptapSuggestion";
import { isCommentEdited } from "./isCommentEdited";
import { getRepliesForRendering } from "./sharedReplyVisibility";
import { cancelReply, submitReply } from "./replySubmission";
import { useSudowriteStore } from "../../stores/sudowriteStore";

let moment = require("moment");
moment.updateLocale("en", {
  relativeTime: {
    future: "in %s",
    past: "%s",
    s: "Now",
    ss: "Now",
    m: "%dm",
    mm: "%dm",
    h: "%dh",
    hh: "%dh",
    d: "%dd",
    dd: "%dd",
    M: "%d mo",
    MM: "%d mo",
    y: "%dy",
    yy: "%dy",
  },
});

export default function Comment({ comment, user, commentsManager, documentId, isMobile = false, commentLocation, installations = [], forceOpen = false }) {
  const commentRef = useRef(null);
  const replySubmitInFlightRef = useRef(false);
  const [isMousedOver, setIsMousedOver] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [newReply, setNewReply] = useState("");
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const comments = useCommentStore((state) => state.comments);
  const tiptap = useCommentStore((state) => state.tiptapEditor);

  const setHoveredAnnotationIds = useCommentStore((state) => state.setHoveredAnnotationIds);
  const hoveredAnnotationIds = useCommentStore((state) => state.hoveredAnnotationIds);
  const activeAnnotationIds = useCommentStore((state) => state.activeAnnotationIds);
  const setActiveAnnotationIds = useCommentStore((state) => state.setActiveAnnotationIds);

  const deleteComment = useCommentStore((state) => state.deleteComment);
  const resolveComment = useCommentStore((state) => state.resolveComment);
  const submitComment = useCommentStore((state) => state.submitComment);

  const revealAnnotation = useCommentStore((state) => state.revealAnnotation);
  const hoverAnnotation = useCommentStore((state) => state.hoverAnnotation);
  const commenter = useCommentStore((state) => state.commenter);
  const displayName = useCommentStore((state) => state.displayName);
  const isSharedDocument = useCommentStore((state) => state.isSharedDocument);
  const hasAccessToAgenticChat = useSudowriteStore((state) => state.subscriptionJSON?.hasAccessToAgenticChat ?? state.subscription?.hasAccessToAgenticChat ?? false);

  const [isEditing, setIsEditing] = useState(false);
  const [isEditingReply, setIsEditingReply] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hideActions, setHideActions] = useState(false);
  const [showDismissReason, setShowDismissReason] = useState(false);
  const [dismissalReason, setDismissalReason] = useState("");
  const [isContentExpanded, setIsContentExpanded] = useState(false);
  const [isContentOverflowing, setIsContentOverflowing] = useState(false);
  const contentRef = useRef(null);
  const { setToast } = useToast();

  const handleApplySuggestion = (comment) => {
    if (!tiptap) return;

    const { id, suggestion, annotationId } = comment;

    try {
      const applied = applySuggestionToEditor(tiptap, suggestion, { annotationId, skipInsertionMark: true });
      if (!applied) {
        console.error("Could not find annotation", annotationId);
        return;
      }
      Auth.fetchWithIdToken(`/api/comments/${id}/accept`);
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          func: "handleApplySuggestion",
        },
      });
    }
  };

  const handleDismissSuggestion = (comment) => {
    setShowDismissReason(true);
  };

  const handleSubmitDismissalFeedback = async (comment) => {
    const { id, annotationId } = comment;

    resolveComment(annotationId, { status: "ignored", persist: false });
    setShowDismissReason(false);

    try {
      await Auth.fetchWithIdToken(`/api/comments/${id}/decline`, {
        method: "POST",
        reason: dismissalReason,
      });

      setDismissalReason("");
    } catch (error) {
      console.error("Error dismissing suggestion:", error);
      setToast("Failed to dismiss suggestion");
    }
  };

  const handleCancelDismissal = () => {
    setShowDismissReason(false);
    setDismissalReason("");
  };

  const handleDismissalReasonKeyDown = (e, comment) => {
    if (e.key === "Enter" && (e.metaKey || !e.shiftKey)) {
      e.preventDefault();
      handleSubmitDismissalFeedback(comment);
    }
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    await submitReply({
      newReply,
      isSubmittingReply,
      replySubmitInFlightRef,
      isMobile,
      setActiveAnnotationIds,
      setToast,
      documentId,
      commentLocation,
      submitComment,
      comment,
      setNewReply,
      setIsSubmittingReply,
      onSubmitStarted: () => setIsClicked(false),
      onSubmitFailed: (reply, message) => {
        setToast(message);
        setNewReply(reply);
        setIsClicked(true);
      },
    });
  };

  const handleReplyCancel = () => {
    cancelReply({
      isSubmittingReply,
      setNewReply,
      onCancel: () => setIsClicked(false),
    });
  };

  const handleExpandContent = (event) => {
    if (!isContentOverflowing || isContentExpanded) {
      return;
    }
    event.stopPropagation();
    setIsContentExpanded(true);
  };

  const handleExpandContentKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    if (!isContentOverflowing || isContentExpanded) {
      return;
    }
    event.preventDefault();
    handleExpandContent(event);
  };

  useEffect(() => {
    if (commenter && displayName?.startsWith("Anonymous") && comments.length === 1 && comment.userId === commenter.uid) {
      setShowTooltip(true);
    } else {
      setShowTooltip(false);
    }
  }, [comments, commenter]);

  // Reveal the annotation when the comment mounts
  useEffect(() => {
    return revealAnnotation(comment.annotationId);
  }, [comments]);

  // Apply hover styles to a mark when the corresponding comment is active
  useEffect(() => {
    if (isMousedOver) {
      return hoverAnnotation(comment.annotationId);
    }
  }, [isMousedOver]);

  // apply hover styles to all related marks when the corresponding comment is active
  useEffect(() => {
    if (hoveredAnnotationIds?.includes(comment.annotationId)) {
      return hoverAnnotation(comment.annotationId);
    }
  }, [hoveredAnnotationIds]);

  useEffect(() => {
    if (activeAnnotationIds?.includes(comment.annotationId)) {
      return hoverAnnotation(comment.annotationId);
    }
  }, [activeAnnotationIds]);

  useEffect(() => {
    if (!forceOpen) return;
    setIsClicked(true);
  }, [forceOpen]);

  useEffect(() => {
    setIsContentExpanded(false);
  }, [comment.content]);

  useEffect(() => {
    if (isEditing || !contentRef.current) {
      setIsContentOverflowing(false);
      return;
    }
    if (isContentExpanded) {
      return;
    }

    const contentEl = contentRef.current;
    const isOverflowing = contentEl.scrollHeight > contentEl.clientHeight + 1;
    setIsContentOverflowing(isOverflowing);
  }, [comment.content, isEditing, isContentExpanded]);

  // Handlers for when the user hovers over the comment
  const handleMouseEnter = () => {
    try {
      setIsMousedOver(true);
      setHoveredAnnotationIds([comment.annotationId]);
    } catch (e) {
      Sentry.captureMessage("Error handleMouseEnter", {
        extra: {
          error: e,
        },
      });
    }
  };

  const handleMouseLeave = () => {
    try {
      setIsMousedOver(false);
      setHoveredAnnotationIds([]);
    } catch (e) {
      Sentry.captureMessage("Error handleMouseLeave", {
        extra: {
          error: e,
        },
      });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest(".reply-box")) return;
      if (commentRef.current && !commentRef.current.contains(event.target)) {
        setIsClicked(false);
      }
    };

    if (commentsManager) {
      commentsManager.registerComment(commentRef.current, comment.annotationId, comment.id);
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (commentsManager) {
        commentsManager.unregisterComment(comment.id);
      }
    };
  }, []);

  let commentDisplayName;
  if (comment.aiId !== null) {
    const { headerLabel } = resolveFeedbackHeader(comment.metadata, installations);
    commentDisplayName = headerLabel;
  } else {
    commentDisplayName = comment.displayName;
  }
  const isEdited = isCommentEdited(comment);
  const visibleReplies = getRepliesForRendering({
    replies: comment.replies,
    isSharedDocument,
  });
  const replyPlaceholder = !isSharedDocument && hasAccessToAgenticChat ? "Comment or @ to ask Sudowrite" : "Reply";
  return (
    <CommentWrapper
      key={comment.id}
      ref={commentRef}
      id={comment.id}
      isMobile={isMobile}
      useAbsolutePosition={!!commentsManager}
      isActive={activeAnnotationIds?.includes(comment.annotationId)}
      className={classNames("comment", { activeComment: hoveredAnnotationIds?.includes(comment.annotationId) })}
      data-annotation-id={comment.annotationId}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => setIsClicked(true)}
    >
      <CommentHeader>
        <SudoTooltip
          text={"Set a name so the author can see who you are."}
          animate
          placement="bottom"
          controlled
          open={showTooltip}
          onOpenChange={(open) => {
            setShowTooltip(open);
          }}
        >
          <CommentInfo>
            {commenter && comment.userId === commenter?.uid ? (
              <>
                <EditableDisplayName
                  setShowTooltip={setShowTooltip}
                  isMobile={isMobile}
                  setHideActions={setHideActions}
                  commentLocation={commentLocation}
                />
                <p className="userIsAuthor">(You)</p>
              </>
            ) : (
              <div className="commentAuthorContainer">
                <div className="commentAuthor">{commentDisplayName}</div>
              </div>
            )}
            <div className="commentTime">{moment(comment.createdAt).fromNow()}</div>
          </CommentInfo>
        </SudoTooltip>
        {isMousedOver && !hideActions && (
          <CommentActions className="commentActions">
            <IconButton
              src="/assets/chat.svg"
              size={12}
              hoverTipText="Reply"
              onClick={() => setIsClicked(true)}
            />
            <IconButton
              src="/assets/check-simple.svg"
              size={12}
              hoverTipText="Resolve"
              onClick={() => resolveComment(comment.annotationId)}
            />
            {commenter?.uid === comment.userId && (
              <DotsActionMenu
                position="bottom-end"
                smallDots="true"
                size={12}
                actions={{
                  unpublish: {
                    icon: (
                      <InlineSVG
                        src="/assets/delete.svg"
                        aria-hidden="true"
                      />
                    ),
                    destructive: true,
                    onClick: () => deleteComment(comment.id, comment.annotationId),
                    label: "Delete Comment",
                  },
                  edit: {
                    icon: (
                      <InlineSVG
                        src="/assets/pencil.svg"
                        aria-hidden="true"
                      />
                    ),
                    onClick: () => setIsEditing(true),
                    label: "Edit Comment",
                  },
                }}
              />
            )}
          </CommentActions>
        )}
      </CommentHeader>
      <CommentContent>
        {isEditing ? (
          <CommentContentEditor
            comment={comment}
            setIsEditing={setIsEditing}
          />
        ) : (
          <>
            <CollapsedMessage
              ref={contentRef}
              className={classNames({
                "is-collapsed": !isContentExpanded,
                "is-expandable": isContentOverflowing && !isContentExpanded,
              })}
              onClick={handleExpandContent}
              onKeyDown={handleExpandContentKeyDown}
              role={isContentOverflowing && !isContentExpanded ? "button" : undefined}
              tabIndex={isContentOverflowing && !isContentExpanded ? 0 : undefined}
              aria-label={isContentOverflowing && !isContentExpanded ? "Expand comment" : undefined}
            >
              {comment.content}
            </CollapsedMessage>
            {isEdited && <div className="commentEdited">Edited</div>}

            {comment.original && (
              <>
                {comment.suggestion !== null && comment.suggestion !== undefined && (
                  <>
                    <div className="h-[1px] border-t border-app-grey-3 my-3 bg-app-grey-3" />
                    <InlineDiffViewer
                      string1={comment.original}
                      string2={comment.suggestion}
                      cleanUp="semantic"
                    />
                  </>
                )}

                <p className="flex items-center gap-2">
                  {!showDismissReason && comment.suggestion && (
                    <SudoButton
                      variant="secondary"
                      size="small"
                      onClick={() => handleApplySuggestion(comment)}
                    >
                      Apply
                    </SudoButton>
                  )}
                  {!showDismissReason ? (
                    <SudoButton
                      variant="tertiaryGrey"
                      size="small"
                      onClick={() => handleDismissSuggestion(comment)}
                    >
                      Dismiss
                    </SudoButton>
                  ) : (
                    <div className="flex my-5 flex-col gap-0 flex-1">
                      <Textarea
                        placeholder="Give feedback on this suggestion to improve the AI."
                        value={dismissalReason}
                        onChange={(e) => setDismissalReason(e.target.value)}
                        onKeyDown={(e) => handleDismissalReasonKeyDown(e, comment)}
                        short
                        className="text-sm w-full"
                        minRows={2}
                      />
                      <div className="flex gap-1">
                        <SudoButton
                          variant="primary"
                          size="small"
                          onClick={() => handleSubmitDismissalFeedback(comment)}
                        >
                          Submit
                        </SudoButton>
                        <SudoButton
                          variant="tertiaryGrey"
                          size="small"
                          onClick={handleCancelDismissal}
                        >
                          Cancel
                        </SudoButton>
                      </div>
                    </div>
                  )}
                </p>
              </>
            )}
          </>
        )}
      </CommentContent>
      {visibleReplies.length > 0 && (
        <CommentReplies>
          {visibleReplies.map((reply) => (
            <Reply
              key={reply.id}
              reply={reply}
              user={user}
              setIsEditingReply={setIsEditingReply}
              isMobile={isMobile}
              commentLocation={commentLocation}
            />
          ))}
        </CommentReplies>
      )}
      {(isMobile || (isClicked && !isEditing && !isEditingReply)) && !showDismissReason && (
        <ReplyBox
          isMobile={isMobile}
          cancelComment={handleReplyCancel}
          submitComment={handleReplySubmit}
          newComment={newReply}
          setNewComment={setNewReply}
          newCommentInputBoxRef={commentRef}
          placeholder={replyPlaceholder}
          buttonText="Reply"
          alwaysShowActionButtons={isMobile}
          isSubmitting={isSubmittingReply}
        />
      )}
    </CommentWrapper>
  );
}
